import moment from 'moment'
import qs from 'qs'
import oikosApi from '../services/oikos'
import { getRegs, update } from './fc'
import { TP_DOC_REC_LIBERACAO_FATURAMENTO } from '../constants/oikosReceitas'

export const PROD_STATUS_TP_FATURAMENTO_AUTORIZADO = 8

export const PROD_STATUS_LABELS = {
  1: 'Não Iniciado',
  2: 'Em Elaboração',
  3: 'Entregue ao EP',
  4: 'Revisão Interna',
  5: 'Gestão Técnica',
  6: 'Entregue ao Cliente',
  7: 'Aprovado pelo Cliente',
  8: 'Faturamento Autorizado',
  9: 'Correção interna',
}

/** Tipos de documento a inativar por etapa (produto). Status 8 usa docs na `rec`. */
const STAGE_PROD_DOC_TYPES = {
  3: [23], // produto final
  4: [30], // revisão
  7: [24], // evidência / parecer cliente
  9: [24],
}

/** Datas de `prod` limpas ao desfazer a etapa. */
const STAGE_PROD_DATE_FIELDS = {
  6: ['prod_dtEntrega'],
  7: ['prod_dtAprov'],
  8: ['prod_dtAutFat'],
}

const num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/** Mesmo critério de cargo do Dashboard de Entregas (`normalizePersonText`). */
function normalizeCargoText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

export function canDesfazerEtapaProduto({ userCargo, permId } = {}) {
  if (Number(permId) === 1) return true
  const cargo = normalizeCargoText(userCargo)
  return (
    cargo.includes('desenvolvedor') ||
    cargo.includes('diretor') ||
    cargo.includes('gerente financeiro') ||
    cargo.includes('gerente tecnico')
  )
}

export function statusLabel(tpId) {
  const id = num(tpId)
  return PROD_STATUS_LABELS[id] || `Etapa ${id || '?'}`
}

/**
 * Carrega o último (e penúltimo) prodStatusAlt ativo do produto.
 */
export async function getUltimosStatusAtivos(prodId) {
  const id = num(prodId)
  if (!id) return { ultimo: null, penultimo: null, lista: [] }

  const res = await oikosApi.get(
    `/?${qs.stringify({
      action: 'get',
      table: 'prodStatusAlt',
      data: {
        prod_id: id,
        prodStatusAlt_ativo: 1,
      },
      fields: {
        '(SELECT prodStatusTp_nome FROM prodStatusTp WHERE prodStatusTp.prodStatusTp_id = prodStatusAlt.prodStatusTp_id LIMIT 1)':
          'prodStatusTp_nome',
      },
      order: 'prodStatusAlt_dtCria DESC, prodStatusAlt_id DESC',
    })}`
  )

  const lista = Array.isArray(res?.data) ? res.data : []
  // API pode não respeitar order; ordenar no cliente
  lista.sort((a, b) => {
    const da = moment(a.prodStatusAlt_dtCria || a.prodStatusAlt_data).valueOf()
    const db = moment(b.prodStatusAlt_dtCria || b.prodStatusAlt_data).valueOf()
    if (db !== da) return db - da
    return num(b.prodStatusAlt_id) - num(a.prodStatusAlt_id)
  })

  return {
    ultimo: lista[0] || null,
    penultimo: lista[1] || null,
    lista,
  }
}

async function softDeleteDocumentoBundle({ documentoVincId, documentoId }) {
  const vincId = num(documentoVincId)
  const docId = num(documentoId)

  if (vincId) {
    await oikosApi.post('/', {
      action: 'update',
      table: 'documentoVinc',
      data: { documentoVinc_id: vincId, documentoVinc_ativo: 0 },
    })
  }

  if (!docId) return

  await oikosApi.post('/', {
    action: 'update',
    table: 'documento',
    data: { documento_id: docId, documento_ativo: 0 },
  })

  const arquivosRes = await oikosApi.get(
    `/?${qs.stringify({
      action: 'get',
      table: 'arquivo',
      data: {
        arquivo_tabRef: 'documento',
        arquivo_tabId: docId,
        arquivo_ativo: 1,
      },
      fields: { arquivo_id: 'arquivo_id' },
    })}`
  )

  for (const arq of arquivosRes?.data || []) {
    const arquivoId = num(arq?.arquivo_id)
    if (!arquivoId) continue
    await oikosApi.post('/', {
      action: 'update',
      table: 'arquivo',
      data: { arquivo_id: arquivoId, arquivo_ativo: 0 },
    })
  }
}

/**
 * Inativa documentos do tipo ligados a tabRef/tabId, preferindo os criados
 * a partir da transição (dtCria do status). Fallback: o mais recente ativo.
 */
async function inativarDocsDaEtapa({ tabRef, tabId, tpDocumentoIds, sinceDtCria }) {
  const ids = (tpDocumentoIds || []).map(num).filter(Boolean)
  const ref = String(tabRef || '')
  const tid = num(tabId)
  if (!ref || !tid || ids.length === 0) return []

  const vincRes = await oikosApi.get(
    `/?${qs.stringify({
      action: 'get',
      table: 'documentoVinc',
      data: {
        documentoVinc_tabRef: ref,
        documentoVinc_tabId: tid,
        documentoVinc_ativo: 1,
      },
      fields: {
        documentoVinc_id: 'documentoVinc_id',
        documento_id: 'documento_id',
        '(SELECT documento_ativo FROM documento WHERE documento.documento_id = documentoVinc.documento_id LIMIT 1)':
          'documento_ativo',
        '(SELECT tpDocumento_id FROM documento WHERE documento.documento_id = documentoVinc.documento_id LIMIT 1)':
          'tpDocumento_id',
        '(SELECT documento_dtCria FROM documento WHERE documento.documento_id = documentoVinc.documento_id LIMIT 1)':
          'documento_dtCria',
      },
    })}`
  )

  const rows = (vincRes?.data || []).filter(
    (d) =>
      Number(d?.documento_ativo) === 1 &&
      ids.includes(num(d?.tpDocumento_id))
  )

  if (rows.length === 0) return []

  const since = sinceDtCria ? moment(sinceDtCria).subtract(2, 'minutes') : null
  let targets = rows
  if (since && since.isValid()) {
    const windowed = rows.filter((d) => {
      const dt = moment(d.documento_dtCria)
      return dt.isValid() && !dt.isBefore(since)
    })
    if (windowed.length > 0) targets = windowed
  }

  // Se não houve filtro por janela, só o mais recente de cada tipo
  if (!since || !since.isValid() || targets === rows) {
    const byType = {}
    for (const d of rows) {
      const tp = num(d.tpDocumento_id)
      const cur = byType[tp]
      if (!cur || num(d.documento_id) > num(cur.documento_id)) byType[tp] = d
    }
    targets = Object.values(byType)
  }

  const inactivated = []
  for (const d of targets) {
    await softDeleteDocumentoBundle({
      documentoVincId: d.documentoVinc_id,
      documentoId: d.documento_id,
    })
    inactivated.push(d)
  }
  return inactivated
}

async function inativarAvaliacoesDoStatus(prodStatusAltId) {
  const sid = num(prodStatusAltId)
  if (!sid) return

  const compliance = await getRegs('prodComplianceAval', { prodStatusAlt_id: sid })
  for (const row of compliance || []) {
    const id = num(row?.prodComplianceAval_id)
    if (id) await update('prodComplianceAval', id, { prodComplianceAval_ativo: 0 })
  }

  const gestao = await getRegs('prodGestaoAval', { prodStatusAlt_id: sid })
  for (const row of gestao || []) {
    const id = num(row?.prodGestaoAval_id)
    if (id) await update('prodGestaoAval', id, { prodGestaoAval_ativo: 0 })
  }
}

async function countOutrosProdutosComStatus8(recId, excludeProdId) {
  const rid = num(recId)
  if (!rid) return 0

  const prods = await getRegs('prod', { rec_id: rid, prod_ativo: 1 })
  let count = 0
  for (const p of prods || []) {
    const pid = num(p?.prod_id)
    if (!pid || pid === num(excludeProdId)) continue
    const { ultimo } = await getUltimosStatusAtivos(pid)
    if (num(ultimo?.prodStatusTp_id) === PROD_STATUS_TP_FATURAMENTO_AUTORIZADO) {
      count += 1
    }
  }
  return count
}

/**
 * Avalia se o botão pode ser usado e por quê.
 * @returns {{ ok: boolean, reason?: string, ultimo?: object, penultimo?: object, etapaNome?: string }}
 */
export async function avaliarDesfazerEtapaProduto(produto) {
  const prodId = num(produto?.prod_id)
  if (!prodId) {
    return { ok: false, reason: 'Produto inválido.' }
  }

  const { ultimo, penultimo } = await getUltimosStatusAtivos(prodId)
  if (!ultimo) {
    return { ok: false, reason: 'Não há etapa anterior para desfazer.' }
  }

  const tp = num(ultimo.prodStatusTp_id)
  const etapaNome = ultimo.prodStatusTp_nome || statusLabel(tp)

  // Sem histórico útil: único registro e ainda no início
  if (!penultimo && (tp <= 1 || tp === 0)) {
    return {
      ok: false,
      reason: 'Não há etapa anterior para desfazer.',
      ultimo,
      penultimo,
      etapaNome,
    }
  }

  if (tp === PROD_STATUS_TP_FATURAMENTO_AUTORIZADO) {
    const recId = num(produto?.rec_id)
    if (recId) {
      const recRows = await getRegs('rec', { rec_id: recId })
      const rec = Array.isArray(recRows) && recRows[0] ? recRows[0] : null
      if (rec && (Number(rec.rec_fat) === 1 || num(rec.nf_id) > 0)) {
        return {
          ok: false,
          reason: 'Não é possível desfazer: receita já faturada.',
          ultimo,
          penultimo,
          etapaNome,
        }
      }
    }
  }

  return {
    ok: true,
    ultimo,
    penultimo,
    etapaNome,
    etapaDestinoNome: penultimo
      ? penultimo.prodStatusTp_nome || statusLabel(penultimo.prodStatusTp_id)
      : 'sem status ativo',
  }
}

/**
 * Executa o desfazer da última etapa ativa do produto.
 */
export async function desfazerEtapaProduto({ produto, userOikosId } = {}) {
  const prodId = num(produto?.prod_id)
  if (!prodId) throw new Error('Produto inválido.')

  const avaliacao = await avaliarDesfazerEtapaProduto(produto)
  if (!avaliacao.ok) {
    throw new Error(avaliacao.reason || 'Não é possível desfazer esta etapa.')
  }

  const ultimo = avaliacao.ultimo
  const statusId = num(ultimo.prodStatusAlt_id)
  const tp = num(ultimo.prodStatusTp_id)
  const since = ultimo.prodStatusAlt_dtCria || ultimo.prodStatusAlt_data

  // 1) Inativar transição de status
  await oikosApi.post('/', {
    action: 'update',
    table: 'prodStatusAlt',
    data: {
      prodStatusAlt_id: statusId,
      prodStatusAlt_ativo: 0,
      prodStatusAlt_usuarioMod: userOikosId || undefined,
    },
  })

  // 2) Avaliações vinculadas
  await inativarAvaliacoesDoStatus(statusId)

  // 3) Documentos da etapa (produto)
  const prodDocTypes = STAGE_PROD_DOC_TYPES[tp]
  if (prodDocTypes) {
    await inativarDocsDaEtapa({
      tabRef: 'prod',
      tabId: prodId,
      tpDocumentoIds: prodDocTypes,
      sinceDtCria: since,
    })
  }

  // 4) Datas / flags no produto
  const prodPatch = {}
  for (const field of STAGE_PROD_DATE_FIELDS[tp] || []) {
    prodPatch[field] = null
  }

  if (tp === 4 || tp === 9) {
    // Reprovação de compliance marca prod_aprovCompliance = 1
    if (Number(produto?.prod_aprovCompliance) === 1) {
      prodPatch.prod_aprovCompliance = 0
    }
  }

  if (Object.keys(prodPatch).length > 0) {
    await oikosApi.post('/', {
      action: 'update',
      table: 'prod',
      data: {
        prod_id: prodId,
        ...prodPatch,
      },
    })
  }

  // 5) Autorização de faturamento na rec
  let recRevertida = false
  if (tp === PROD_STATUS_TP_FATURAMENTO_AUTORIZADO) {
    const recId = num(produto?.rec_id)
    if (recId) {
      const outrosCom8 = await countOutrosProdutosComStatus8(recId, prodId)
      if (outrosCom8 === 0) {
        await oikosApi.post('/', {
          action: 'update',
          table: 'rec',
          data: {
            rec_id: recId,
            rec_aut: 0,
            rec_dtLibAutNf: null,
            rec_vlrLibAutNf: null,
            rec_justDifLibAutNf: null,
          },
        })
        await inativarDocsDaEtapa({
          tabRef: 'rec',
          tabId: recId,
          tpDocumentoIds: [TP_DOC_REC_LIBERACAO_FATURAMENTO],
          sinceDtCria: since,
        })
        recRevertida = true
      }
    }
  }

  return {
    prodId,
    statusDesfeitoId: statusId,
    statusDesfeitoTp: tp,
    etapaNome: avaliacao.etapaNome,
    etapaDestinoNome: avaliacao.etapaDestinoNome,
    recRevertida,
  }
}
